// src/components/proposal/GenerationProgress.tsx
// Progress tracking component for proposal generation

import React from 'react';
import {
  FileText,
  BookOpen,
  Settings,
  Users,
  History,
  DollarSign,
  Shield,
  Check,
  Loader2,
  Circle,
  AlertCircle,
  Package
} from 'lucide-react';
import { GenerationProgress } from '../../services/orchestrated-proposal-service';

interface GenerationProgressProps {
  progress: GenerationProgress;
}

// Order matches the actual generation pipeline: the deliverables backbone runs
// first (it grounds everything), then the substantive sections, and the Cover
// Letter + Executive Summary are written LAST so they summarize the real proposal.
const SECTION_CONFIG = {
  deliverables: {
    label: 'Deliverables, Labor & Cost Backbone',
    icon: Package,
    description: 'Extracting deliverables and building the labor/cost basis'
  },
  technical: {
    label: 'Technical Approach',
    icon: Settings,
    description: 'Methodology and solution, organized by the PWS tasks'
  },
  management: {
    label: 'Management Approach',
    icon: Users,
    description: 'Organization, roles, and quality management'
  },
  past_performance: {
    label: 'Past Performance',
    icon: History,
    description: 'Relevant contract history and achievements'
  },
  personnel: {
    label: 'Key Personnel',
    icon: Users,
    description: 'Required roles matched to your team'
  },
  pricing: {
    label: 'Pricing Strategy',
    icon: DollarSign,
    description: 'CLIN pricing aligned to the deliverables'
  },
  compliance: {
    label: 'Compliance',
    icon: Shield,
    description: 'Reps, certs, and clause compliance'
  },
  executive_summary: {
    label: 'Executive Summary',
    icon: BookOpen,
    description: 'Summarizes the drafted proposal'
  },
  cover_letter: {
    label: 'Cover Letter',
    icon: FileText,
    description: 'Introduces the finished proposal'
  }
};

export const GenerationProgressPanel: React.FC<GenerationProgressProps> = ({
  progress
}) => {
  const allSections = Object.keys(SECTION_CONFIG);

  const getSectionStatus = (section: string): 'pending' | 'active' | 'complete' | 'error' => {
    if (progress.completedSections.includes(section)) return 'complete';
    if (progress.currentSection === section) return 'active';
    if (progress.phase === 'error') return 'error';
    return 'pending';
  };

  const getStatusIcon = (status: 'pending' | 'active' | 'complete' | 'error') => {
    switch (status) {
      case 'complete':
        return <Check className="w-5 h-5 text-success" />;
      case 'active':
        return <Loader2 className="w-5 h-5 text-bw-primary animate-spin" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-urgent" />;
      default:
        return <Circle className="w-5 h-5 text-gray-300" />;
    }
  };

  const getPhaseMessage = () => {
    switch (progress.phase) {
      case 'analyzing':
        return 'Analyzing requirements and profile data...';
      case 'enriching':
        return 'Processing additional data...';
      case 'generating':
        return progress.message || 'Generating proposal sections...';
      case 'complete':
        return 'Proposal generation complete!';
      case 'error':
        return progress.message || 'An error occurred';
      default:
        return 'Preparing...';
    }
  };

  return (
    <div className="bg-bw-surface rounded-xl border p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-semibold text-bw-ink text-lg">Generating Proposal</h3>
        <span className="text-bw-muted">
          {progress.completedSections.length} / {progress.totalSections} sections
        </span>
      </div>

      {/* Overall progress */}
      <div className="space-y-2 mb-6">
        <div className="flex items-center justify-between text-sm">
          <span className="text-bw-muted">{getPhaseMessage()}</span>
          <span className="font-medium">{Math.round(progress.percentage)}%</span>
        </div>
        <div className="w-full bg-bw-surface-subtle rounded-full h-2">
          <div
            className="bg-slate-800 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress.percentage}%` }}
          />
        </div>
      </div>

      {/* Section progress */}
      <div className="space-y-3">
        {allSections.map((section) => {
          const config = SECTION_CONFIG[section as keyof typeof SECTION_CONFIG];
          const status = getSectionStatus(section);
          const Icon = config.icon;

          return (
            <div
              key={section}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                status === 'active'
                  ? 'bg-bw-primary-tint border-bw-primary/20'
                  : status === 'complete'
                  ? 'bg-success-bg border-success-border'
                  : status === 'error'
                  ? 'bg-urgent-bg border-urgent-border'
                  : 'bg-bw-surface-subtle border-bw-border'
              }`}
            >
              <div className={`p-2 rounded-lg ${
                status === 'active'
                  ? 'bg-bw-primary-tint'
                  : status === 'complete'
                  ? 'bg-success-bg'
                  : status === 'error'
                  ? 'bg-urgent-bg'
                  : 'bg-bw-surface-subtle'
              }`}>
                <Icon className={`w-5 h-5 ${
                  status === 'active'
                    ? 'text-bw-primary'
                    : status === 'complete'
                    ? 'text-success'
                    : status === 'error'
                    ? 'text-urgent'
                    : 'text-bw-muted'
                }`} />
              </div>

              <div className="flex-1">
                <div className="font-medium text-sm">
                  {config.label}
                </div>
                <div className="text-xs text-bw-muted">
                  {config.description}
                </div>
              </div>

              {getStatusIcon(status)}
            </div>
          );
        })}
      </div>

      {/* Completion message */}
      {progress.phase === 'complete' && (
        <div className="mt-6 p-4 bg-success-bg border border-success-border rounded-lg text-center">
          <Check className="w-8 h-8 text-success mx-auto mb-2" />
          <p className="font-medium text-success">
            All sections generated successfully!
          </p>
          <p className="text-sm text-success mt-1">
            Your proposal is ready for review.
          </p>
        </div>
      )}

      {/* Error message */}
      {progress.phase === 'error' && (
        <div className="mt-6 p-4 bg-urgent-bg border border-urgent-border rounded-lg text-center">
          <AlertCircle className="w-8 h-8 text-urgent mx-auto mb-2" />
          <p className="font-medium text-urgent">
            Generation encountered an error
          </p>
          <p className="text-sm text-urgent mt-1">
            {progress.message}
          </p>
        </div>
      )}
    </div>
  );
};

export default GenerationProgressPanel;
